import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Eye,
  ImagePlus,
  Plus,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
  Wand2,
} from "lucide-react";
import "./styles.css";
import {
  BrandRecord,
  deleteBrand,
  fetchBrands,
  hasSupabaseBrandConfig,
  saveBrand,
} from "../../integrations/brandConfigApi";
import {
  ClientConfigDraft,
  cloneClientConfigDraft,
  defaultClientConfigDraft,
  normalizeBrandSlug,
} from "../../integrations/clientConfig";
import { detectPaletteFromLogo } from "./logoTheme";
import { BRAND_THEME_PRESETS } from "./themePresets";

const PALETTE_FIELDS = [
  { key: "pageBackgroundStart", label: "Фон: верх", description: "Стартовый тон фона" },
  { key: "pageBackground", label: "Фон: центр", description: "Основной тон фона" },
  { key: "pageBackgroundEnd", label: "Фон: низ", description: "Финишный тон фона" },
  { key: "surface", label: "Surface", description: "Фон карточек и блоков" },
  { key: "surfaceAlt", label: "Surface Alt", description: "Фон инпутов и табов" },
  { key: "surfaceElevated", label: "Surface Elevated", description: "Поднятые поверхности" },
  { key: "border", label: "Border", description: "Границы блоков" },
  { key: "accent", label: "Accent", description: "Основной бренд-акцент" },
  { key: "accentStrong", label: "Accent Strong", description: "Усиленный акцент" },
  { key: "accentContrast", label: "Accent Contrast", description: "Текст на акценте" },
  { key: "textPrimary", label: "Text Primary", description: "Основной текст" },
  { key: "textSecondary", label: "Text Secondary", description: "Вторичный текст" },
  { key: "gridLine", label: "Grid Line", description: "Линии фоновой сетки" },
  { key: "glowPrimary", label: "Glow Primary", description: "Главное свечение" },
  { key: "glowSecondary", label: "Glow Secondary", description: "Доп. свечение" },
  { key: "badgeBackground", label: "Badge Background", description: "Фон счетчика" },
  { key: "badgeText", label: "Badge Text", description: "Текст счетчика" },
  { key: "tabActiveBackground", label: "Active Tab Bg", description: "Фон активного таба" },
  { key: "tabActiveText", label: "Active Tab Text", description: "Текст активного таба" },
  { key: "inputBackground", label: "Input Background", description: "Фон полей ввода" },
  {
    key: "secondaryButtonBackground",
    label: "Secondary Btn Bg",
    description: "Фон вторичных кнопок",
  },
  {
    key: "secondaryButtonText",
    label: "Secondary Btn Text",
    description: "Текст вторичных кнопок",
  },
  {
    key: "secondaryButtonBorder",
    label: "Secondary Btn Border",
    description: "Граница вторичных кнопок",
  },
  { key: "skeletonBase", label: "Skeleton Base", description: "База скелетона" },
  {
    key: "skeletonHighlight",
    label: "Skeleton Highlight",
    description: "Подсветка скелетона",
  },
] as const;

const makeDraft = (draft?: ClientConfigDraft): ClientConfigDraft => {
  const base = cloneClientConfigDraft(defaultClientConfigDraft);
  const next = cloneClientConfigDraft(draft || defaultClientConfigDraft);

  return {
    ...base,
    ...next,
    id: next.id || "",
    displayName: next.displayName || "",
  };
};

const EMPTY_BRAND_DRAFT = makeDraft({
  ...defaultClientConfigDraft,
  id: "",
  displayName: "",
  showcase: {
    ...defaultClientConfigDraft.showcase,
    logoText: "",
    logoSrc: "",
    logoAlt: "",
    heroLabel: "",
    heroTitle: "",
    heroDescription: "",
  },
});

const toRecordFromDraft = (draft: ClientConfigDraft): BrandRecord => ({
  slug: normalizeBrandSlug(draft.id),
  display_name: draft.displayName || draft.id || "",
  brand_data: draft,
});

const ColorField = ({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
}) => {
  const inputValue = value || "#000000";
  const pickerValue = /^#([0-9a-f]{6}|[0-9a-f]{3})$/i.test(inputValue)
    ? inputValue
    : "#000000";

  return (
    <label className="brand-studio_color-card">
      <div className="brand-studio_field-meta">
        <span>{label}</span>
        <small>{description}</small>
      </div>
      <div className="brand-studio_color-controls">
        <input
          type="color"
          value={pickerValue}
          onChange={(event) => onChange(event.target.value)}
        />
        <input
          type="text"
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
          placeholder="#101828 или rgba(255,255,255,0.12)"
        />
      </div>
    </label>
  );
};

export const BrandStudio = () => {
  const [records, setRecords] = useState<BrandRecord[]>([]);
  const [selectedSlug, setSelectedSlug] = useState<string>("kub");
  const [form, setForm] = useState<ClientConfigDraft>(makeDraft(EMPTY_BRAND_DRAFT));
  const [pending, setPending] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [detectingTheme, setDetectingTheme] = useState(false);
  const [generatedThemeTone, setGeneratedThemeTone] = useState<"dark" | "light">("dark");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadBrands = useCallback(async () => {
    setPending(true);
    setErrorMessage(null);

    try {
      const items = await fetchBrands();
      setRecords(items);
      setSelectedSlug((current) => {
        if (items.some((item) => item.slug === current)) {
          return current;
        }

        return items[0]?.slug || "new";
      });
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Не удалось загрузить бренды.",
      );
    } finally {
      setPending(false);
    }
  }, []);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  const selectedRecord = useMemo(
    () => records.find((item) => item.slug === selectedSlug) || null,
    [records, selectedSlug],
  );

  useEffect(() => {
    if (selectedRecord?.brand_data) {
      setForm(makeDraft(selectedRecord.brand_data));
      return;
    }

    if (selectedSlug === "new") {
      setForm(makeDraft(EMPTY_BRAND_DRAFT));
    }
  }, [selectedRecord, selectedSlug]);

  const resetMessages = () => {
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const handleCreate = () => {
    resetMessages();
    setSelectedSlug("new");
    setForm(makeDraft(EMPTY_BRAND_DRAFT));
  };

  const setField = <K extends keyof ClientConfigDraft>(field: K, value: ClientConfigDraft[K]) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const setNestedTabField = (
    type: "residential" | "commerce",
    field: "title" | "description" | "emptyTitle" | "emptyDescription",
    value: string,
  ) => {
    setForm((current) => ({
      ...current,
      housingsPage: {
        ...current.housingsPage,
        tabs: {
          ...current.housingsPage?.tabs,
          [type]: {
            ...current.housingsPage?.tabs?.[type],
            [field]: value,
          },
        },
      },
    }));
  };

  const setPaletteField = (field: string, value: string) => {
    setForm((current) => ({
      ...current,
      showcase: {
        ...current.showcase,
        palette: {
          ...current.showcase?.palette,
          [field]: value,
        },
      },
    }));
  };

  const setShowcaseField = (
    field: keyof NonNullable<ClientConfigDraft["showcase"]>,
    value: any,
  ) => {
    setForm((current) => ({
      ...current,
      showcase: {
        ...current.showcase,
        [field]: value,
      },
    }));
  };

  const setPricingField = (
    field: keyof NonNullable<ClientConfigDraft["pricing"]>,
    value: any,
  ) => {
    setForm((current) => ({
      ...current,
      pricing: {
        ...current.pricing,
        [field]: value,
      },
    }));
  };

  const setHousingDetailsField = (
    field: keyof NonNullable<ClientConfigDraft["housingDetails"]>,
    value: any,
  ) => {
    setForm((current) => ({
      ...current,
      housingDetails: {
        ...current.housingDetails,
        [field]: value,
      },
    }));
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Не удалось прочитать файл логотипа."));
      reader.readAsDataURL(file);
    });

    setForm((current) => ({
      ...current,
      showcase: {
        ...current.showcase,
        logoMode: "image",
        logoSrc: dataUrl,
      },
    }));
  };

  const applyPalettePreset = (presetId: string) => {
    const preset = BRAND_THEME_PRESETS.find((item) => item.id === presetId);

    if (!preset) {
      return;
    }

    resetMessages();
    setForm((current) => ({
      ...current,
      showcase: {
        ...current.showcase,
        palette: {
          ...current.showcase?.palette,
          ...preset.palette,
        },
      },
    }));
    setSuccessMessage(`Применена цветовая схема ${preset.name}.`);
  };

  const handleAutoDetectTheme = async () => {
    const logoSrc = form.showcase?.logoSrc;

    if (!logoSrc) {
      setErrorMessage("Сначала укажи URL логотипа или загрузи файл.");
      return;
    }

    setDetectingTheme(true);
    resetMessages();

    try {
      const { palette, logoTextColor } = await detectPaletteFromLogo(
        logoSrc,
        generatedThemeTone,
      );
      setForm((current) => ({
        ...current,
        showcase: {
          ...current.showcase,
          logoTextColor,
          palette: {
            ...current.showcase?.palette,
            ...palette,
          },
        },
      }));
      setSuccessMessage(
        `Палитра ${generatedThemeTone === "light" ? "светлой" : "тёмной"} темы собрана по логотипу.`,
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Не удалось определить палитру по логотипу.",
      );
    } finally {
      setDetectingTheme(false);
    }
  };

  const handleSave = async () => {
    const slug = normalizeBrandSlug(form.id);

    if (!slug) {
      setErrorMessage("Укажите slug бренда. Например: kub или partner-a.");
      return;
    }

    if (!form.displayName?.trim()) {
      setErrorMessage("Укажите отображаемое название бренда.");
      return;
    }

    setSaving(true);
    resetMessages();

    try {
      const saved = await saveBrand({
        ...(selectedRecord || toRecordFromDraft(form)),
        slug,
        display_name: form.displayName,
        brand_data: {
          ...form,
          id: slug,
          displayName: form.displayName.trim(),
        },
      });

      await loadBrands();
      setSelectedSlug(saved.slug);
      setSuccessMessage("Бренд сохранён в Supabase.");
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Не удалось сохранить бренд.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord) {
      return;
    }

    setDeleting(true);
    resetMessages();

    try {
      await deleteBrand(selectedRecord);
      await loadBrands();
      setSelectedSlug("new");
      setForm(makeDraft(EMPTY_BRAND_DRAFT));
      setSuccessMessage("Бренд удалён из Supabase.");
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Не удалось удалить бренд.",
      );
    } finally {
      setDeleting(false);
    }
  };

  const enabledHousingTypes = form.showcase?.enabledHousingTypes || ["residential", "commerce"];
  const previewHref = `/brand=${normalizeBrandSlug(form.id) || "brand"}`;
  const isBuiltinOnly = Boolean(
    selectedRecord?.id && String(selectedRecord.id).startsWith("builtin:"),
  );

  return (
    <div className="brand-studio">
      <div className="brand-studio_shell">
        <header className="brand-studio_header">
          <div>
            <div className="brand-studio_eyebrow">Brand Studio</div>
            <h1>Настройка брендированных витрин</h1>
            <p>
              Здесь можно настраивать slug, отображаемое имя, палитру, тексты,
              логотип и затем сохранять бренд в Supabase для витрины вида
              <code>{previewHref}</code>.
            </p>
          </div>

          <div className="brand-studio_header-actions">
            <button type="button" onClick={loadBrands} className="brand-studio_action ghost">
              <RefreshCw size={16} />
              Обновить
            </button>
            <Link className="brand-studio_action" to={previewHref}>
              <Eye size={16} />
              Открыть витрину
            </Link>
          </div>
        </header>

        {!hasSupabaseBrandConfig ? (
          <div className="brand-studio_notice warning">
            Не найдены <code>REACT_APP_SUPABASE_URL</code> и{" "}
            <code>REACT_APP_SUPABASE_ANON_KEY</code>. Редактор работает в локальном
            режиме, но кнопка сохранения в Supabase будет недоступна.
          </div>
        ) : null}

        {errorMessage ? <div className="brand-studio_notice error">{errorMessage}</div> : null}
        {successMessage ? (
          <div className="brand-studio_notice success">{successMessage}</div>
        ) : null}

        <div className="brand-studio_layout">
          <aside className="brand-studio_sidebar">
            <div className="brand-studio_sidebar-header">
              <h2>Бренды</h2>
              <button type="button" onClick={handleCreate} className="brand-studio_icon-button">
                <Plus size={16} />
              </button>
            </div>

            {pending ? (
              <div className="brand-studio_empty">Загружаю список брендов…</div>
            ) : (
              <div className="brand-studio_brand-list">
                {records.map((record) => (
                  <button
                    type="button"
                    key={record.slug}
                    className={`brand-studio_brand-item ${
                      selectedSlug === record.slug ? "is-active" : ""
                    }`}
                    onClick={() => {
                      resetMessages();
                      setSelectedSlug(record.slug);
                    }}
                  >
                    <strong>{record.display_name || record.slug}</strong>
                    <span>{record.slug}</span>
                  </button>
                ))}
              </div>
            )}
          </aside>

          <main className="brand-studio_main">
            <section className="brand-studio_card">
              <div className="brand-studio_section-head">
                <div>
                  <h2>Основное</h2>
                  <p>Slug идёт в URL, display name нужен для человеческого отображения.</p>
                </div>
                <div className="brand-studio_top-actions">
                  <button
                    type="button"
                    className="brand-studio_action"
                    disabled={!hasSupabaseBrandConfig || saving}
                    onClick={handleSave}
                  >
                    <Save size={16} />
                    {saving ? "Сохраняю…" : "Сохранить"}
                  </button>
                  <button
                    type="button"
                    className="brand-studio_action danger"
                    disabled={deleting || !selectedRecord || isBuiltinOnly}
                    onClick={handleDelete}
                  >
                    <Trash2 size={16} />
                    {deleting ? "Удаляю…" : "Удалить"}
                  </button>
                </div>
              </div>

              <div className="brand-studio_grid two">
                <label className="brand-studio_field">
                  <span>Slug</span>
                  <input
                    type="text"
                    value={form.id || ""}
                    onChange={(event) => setField("id", normalizeBrandSlug(event.target.value))}
                    placeholder="kub"
                  />
                </label>

                <label className="brand-studio_field">
                  <span>Отображаемое имя</span>
                  <input
                    type="text"
                    value={form.displayName || ""}
                    onChange={(event) => setField("displayName", event.target.value)}
                    placeholder="Куб"
                  />
                </label>

                <label className="brand-studio_field">
                  <span>Поиск placeholder</span>
                  <input
                    type="text"
                    value={form.searchPlaceholder || ""}
                    onChange={(event) => setField("searchPlaceholder", event.target.value)}
                  />
                </label>

                <label className="brand-studio_field">
                  <span>Подпись кнопки карточки</span>
                  <input
                    type="text"
                    value={form.housingCard?.openLabel || ""}
                    onChange={(event) =>
                      setField("housingCard", {
                        ...form.housingCard,
                        openLabel: event.target.value,
                      })
                    }
                  />
                </label>
              </div>
            </section>

            <section className="brand-studio_card">
              <div className="brand-studio_section-head">
                <div>
                  <h2>Логотип и hero</h2>
                  <p>Можно использовать текстовый логотип, URL картинки или загрузку файла.</p>
                </div>
                <button
                  type="button"
                  className="brand-studio_action ghost"
                  onClick={handleAutoDetectTheme}
                  disabled={detectingTheme}
                >
                  <Wand2 size={16} />
                  {detectingTheme ? "Определяю палитру…" : "Палитра по логотипу"}
                </button>
              </div>

              <div className="brand-studio_toggle-row brand-studio_compact-row">
                <label className="brand-studio_checkbox">
                  <input
                    type="radio"
                    name="generated-theme-tone"
                    checked={generatedThemeTone === "dark"}
                    onChange={() => setGeneratedThemeTone("dark")}
                  />
                  <span>Генерировать тёмную</span>
                </label>
                <label className="brand-studio_checkbox">
                  <input
                    type="radio"
                    name="generated-theme-tone"
                    checked={generatedThemeTone === "light"}
                    onChange={() => setGeneratedThemeTone("light")}
                  />
                  <span>Генерировать светлую</span>
                </label>
              </div>

              <div className="brand-studio_grid two">
                <label className="brand-studio_field">
                  <span>Режим логотипа</span>
                  <select
                    value={form.showcase?.logoMode || "text"}
                    onChange={(event) => setShowcaseField("logoMode", event.target.value)}
                  >
                    <option value="text">Текст</option>
                    <option value="image">Картинка</option>
                    <option value="image-text">Картинка + текст</option>
                  </select>
                </label>

                <label className="brand-studio_field">
                  <span>Расположение текста</span>
                  <select
                    value={form.showcase?.logoLayout || "text-right"}
                    onChange={(event) => setShowcaseField("logoLayout", event.target.value)}
                  >
                    <option value="text-right">Текст справа от картинки</option>
                    <option value="text-bottom">Текст снизу от картинки</option>
                  </select>
                </label>

                <label className="brand-studio_field">
                  <span>Текст логотипа</span>
                  <input
                    type="text"
                    value={form.showcase?.logoText || ""}
                    onChange={(event) => setShowcaseField("logoText", event.target.value)}
                  />
                </label>

                <label className="brand-studio_field">
                  <span>URL логотипа</span>
                  <input
                    type="text"
                    value={form.showcase?.logoSrc || ""}
                    onChange={(event) => setShowcaseField("logoSrc", event.target.value)}
                    placeholder="https://... или data:image/..."
                  />
                </label>

                <label className="brand-studio_field">
                  <span>Alt логотипа</span>
                  <input
                    type="text"
                    value={form.showcase?.logoAlt || ""}
                    onChange={(event) => setShowcaseField("logoAlt", event.target.value)}
                  />
                </label>

                <label className="brand-studio_field">
                  <span>Ширина логотипа, px</span>
                  <input
                    type="number"
                    min="80"
                    max="520"
                    value={form.showcase?.logoWidth || 240}
                    onChange={(event) =>
                      setShowcaseField("logoWidth", Number(event.target.value) || 240)
                    }
                  />
                </label>

                <label className="brand-studio_field">
                  <span>Высота картинки, px</span>
                  <input
                    type="number"
                    min="20"
                    max="180"
                    value={form.showcase?.logoImageHeight || 52}
                    onChange={(event) =>
                      setShowcaseField(
                        "logoImageHeight",
                        Number(event.target.value) || 52,
                      )
                    }
                  />
                </label>

                <label className="brand-studio_field">
                  <span>Размер текста, px</span>
                  <input
                    type="number"
                    min="12"
                    max="72"
                    value={form.showcase?.logoTextSize || 34}
                    onChange={(event) =>
                      setShowcaseField("logoTextSize", Number(event.target.value) || 34)
                    }
                  />
                </label>
              </div>

              <div className="brand-studio_grid two">
                <ColorField
                  label="Цвет текста логотипа"
                  description="Отдельный цвет для текстовой части логотипа"
                  value={form.showcase?.logoTextColor || ""}
                  onChange={(value) => setShowcaseField("logoTextColor", value)}
                />
              </div>

              <label className="brand-studio_upload">
                <ImagePlus size={18} />
                <span>Загрузить логотип файлом</span>
                <input type="file" accept="image/*" onChange={handleLogoUpload} />
              </label>

              <div className="brand-studio_grid two">
                <label className="brand-studio_field">
                  <span>Hero label</span>
                  <input
                    type="text"
                    value={form.showcase?.heroLabel || ""}
                    onChange={(event) => setShowcaseField("heroLabel", event.target.value)}
                  />
                </label>

                <label className="brand-studio_field">
                  <span>Hero title</span>
                  <input
                    type="text"
                    value={form.showcase?.heroTitle || ""}
                    onChange={(event) => setShowcaseField("heroTitle", event.target.value)}
                  />
                </label>
              </div>

              <label className="brand-studio_field">
                <span>Hero description</span>
                <textarea
                  value={form.showcase?.heroDescription || ""}
                  onChange={(event) => setShowcaseField("heroDescription", event.target.value)}
                  rows={3}
                />
              </label>

              <div className="brand-studio_grid two">
                <label className="brand-studio_field">
                  <span>Имя контакта бренда</span>
                  <input
                    type="text"
                    value={form.showcase?.contactName || ""}
                    onChange={(event) => setShowcaseField("contactName", event.target.value)}
                    placeholder="Отдел продаж KUB"
                  />
                </label>

                <label className="brand-studio_field">
                  <span>Телефон бренда</span>
                  <input
                    type="text"
                    value={form.showcase?.phone || ""}
                    onChange={(event) => setShowcaseField("phone", event.target.value)}
                    placeholder="+79991234567"
                  />
                </label>
              </div>

              <div className="brand-studio_grid two">
                <label className="brand-studio_field">
                  <span>WhatsApp номер для расчёта</span>
                  <input
                    type="text"
                    value={form.showcase?.whatsappNumber || ""}
                    onChange={(event) =>
                      setShowcaseField("whatsappNumber", event.target.value)
                    }
                    placeholder="+79991234567"
                  />
                </label>

                <label className="brand-studio_field">
                  <span>Подпись кнопки WhatsApp</span>
                  <input
                    type="text"
                    value={form.showcase?.whatsappButtonLabel || ""}
                    onChange={(event) =>
                      setShowcaseField("whatsappButtonLabel", event.target.value)
                    }
                    placeholder="Отправить расчёт в WhatsApp"
                  />
                </label>
              </div>

              <div className="brand-studio_grid two">
                <label className="brand-studio_field">
                  <span>WhatsApp бренда</span>
                  <input
                    type="text"
                    value={form.showcase?.whatsapp || ""}
                    onChange={(event) => setShowcaseField("whatsapp", event.target.value)}
                    placeholder="+79991234567"
                  />
                </label>

                <label className="brand-studio_field">
                  <span>Telegram бренда</span>
                  <input
                    type="text"
                    value={form.showcase?.telegram || ""}
                    onChange={(event) => setShowcaseField("telegram", event.target.value)}
                    placeholder="@brand"
                  />
                </label>
              </div>

              <div className="brand-studio_grid two">
                <label className="brand-studio_field">
                  <span>Instagram бренда</span>
                  <input
                    type="text"
                    value={form.showcase?.instagram || ""}
                    onChange={(event) => setShowcaseField("instagram", event.target.value)}
                    placeholder="@brand"
                  />
                </label>
              </div>
            </section>

            <section className="brand-studio_card">
              <div className="brand-studio_section-head">
                <div>
                  <h2>Сценарии и тексты</h2>
                  <p>Отдельно настраиваются жилая и коммерческая вкладки.</p>
                </div>
              </div>

              <div className="brand-studio_toggle-row">
                {(["residential", "commerce"] as const).map((type) => {
                  const checked = enabledHousingTypes.includes(type);
                  return (
                    <label key={type} className="brand-studio_checkbox">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) => {
                          const next = event.target.checked
                            ? [...new Set([...enabledHousingTypes, type])]
                            : enabledHousingTypes.filter((item) => item !== type);
                          setShowcaseField("enabledHousingTypes", next);
                        }}
                      />
                      <span>{type === "residential" ? "Жилая" : "Коммерческая"}</span>
                    </label>
                  );
                })}
              </div>

              <div className="brand-studio_grid two">
                <label className="brand-studio_field">
                  <span>Жилая: title</span>
                  <input
                    type="text"
                    value={form.housingsPage?.tabs?.residential?.title || ""}
                    onChange={(event) =>
                      setNestedTabField("residential", "title", event.target.value)
                    }
                  />
                </label>
                <label className="brand-studio_field">
                  <span>Коммерческая: title</span>
                  <input
                    type="text"
                    value={form.housingsPage?.tabs?.commerce?.title || ""}
                    onChange={(event) =>
                      setNestedTabField("commerce", "title", event.target.value)
                    }
                  />
                </label>
                <label className="brand-studio_field">
                  <span>Жилая: description</span>
                  <textarea
                    rows={3}
                    value={form.housingsPage?.tabs?.residential?.description || ""}
                    onChange={(event) =>
                      setNestedTabField("residential", "description", event.target.value)
                    }
                  />
                </label>
                <label className="brand-studio_field">
                  <span>Коммерческая: description</span>
                  <textarea
                    rows={3}
                    value={form.housingsPage?.tabs?.commerce?.description || ""}
                    onChange={(event) =>
                      setNestedTabField("commerce", "description", event.target.value)
                    }
                  />
                </label>
              </div>
            </section>

            <section className="brand-studio_card">
              <div className="brand-studio_section-head">
                <div>
                  <h2>Блок цен и детали</h2>
                  <p>Управление калькулятором, ценами и вторичными разделами карточки.</p>
                </div>
              </div>

              <div className="brand-studio_toggle-row">
                <label className="brand-studio_checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(form.pricing?.showPriceList)}
                    onChange={(event) =>
                      setPricingField("showPriceList", event.target.checked)
                    }
                  />
                  <span>Показывать price list</span>
                </label>
                <label className="brand-studio_checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(form.pricing?.showCalculator)}
                    onChange={(event) =>
                      setPricingField("showCalculator", event.target.checked)
                    }
                  />
                  <span>Показывать калькулятор</span>
                </label>
                <label className="brand-studio_checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(form.housingDetails?.showMaterialsSection)}
                    onChange={(event) =>
                      setHousingDetailsField("showMaterialsSection", event.target.checked)
                    }
                  />
                  <span>Материалы</span>
                </label>
                <label className="brand-studio_checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(form.housingDetails?.showNavigationSection)}
                    onChange={(event) =>
                      setHousingDetailsField("showNavigationSection", event.target.checked)
                    }
                  />
                  <span>Навигация</span>
                </label>
                <label className="brand-studio_checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(form.housingDetails?.showFactsSection)}
                    onChange={(event) =>
                      setHousingDetailsField("showFactsSection", event.target.checked)
                    }
                  />
                  <span>Факты</span>
                </label>
              </div>

              <div className="brand-studio_grid two">
                <label className="brand-studio_field">
                  <span>Eyebrow калькулятора</span>
                  <input
                    type="text"
                    value={form.pricing?.eyebrow || ""}
                    onChange={(event) => setPricingField("eyebrow", event.target.value)}
                  />
                </label>
                <label className="brand-studio_field">
                  <span>Заголовок калькулятора</span>
                  <input
                    type="text"
                    value={form.pricing?.title || ""}
                    onChange={(event) => setPricingField("title", event.target.value)}
                  />
                </label>
              </div>
            </section>

            <section className="brand-studio_card">
              <div className="brand-studio_section-head">
                <div>
                  <h2>Палитра</h2>
                  <p>Для каждого цвета есть picker и ручной ввод. Ниже есть готовые стартовые схемы.</p>
                </div>
              </div>

              <div className="brand-studio_presets">
                {BRAND_THEME_PRESETS.map((preset) => (
                  <button
                    type="button"
                    key={preset.id}
                    className="brand-studio_preset-card"
                    onClick={() => applyPalettePreset(preset.id)}
                  >
                    <div className="brand-studio_preset-top">
                      <div>
                        <strong>{preset.name}</strong>
                        <span>{preset.description}</span>
                      </div>
                      <div className="brand-studio_preset-badge">
                        <Sparkles size={14} />
                        <em>{preset.tone === "light" ? "light" : "dark"}</em>
                      </div>
                    </div>
                    <div className="brand-studio_preset-swatches">
                      <span style={{ background: preset.palette.pageBackground }} />
                      <span style={{ background: preset.palette.surface }} />
                      <span style={{ background: preset.palette.accent }} />
                      <span style={{ background: preset.palette.accentStrong }} />
                    </div>
                  </button>
                ))}
              </div>

              <div className="brand-studio_colors">
                {PALETTE_FIELDS.map((field) => (
                  <ColorField
                    key={field.key}
                    label={field.label}
                    description={field.description}
                    value={(form.showcase?.palette as any)?.[field.key] || ""}
                    onChange={(value) => setPaletteField(field.key, value)}
                  />
                ))}
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
};
